/**
 * HISI-006: Futuristic Executions Page - Mission Control Interface
 * Grid-based mission cards replacing boring tables
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDashboardServerStore } from '@/stores/dashboard-server';
import { useServerAuth } from '@/hooks/useAuth-cookie';
import AtmosphericBackground from '@/components/futuristic/AtmosphericBackground';
import MissionCard from '@/components/futuristic/MissionCard';
import HolographicCard, { CardContent } from '@/components/futuristic/HolographicCard';
import StatsBar from '@/components/futuristic/StatsBar';
import FuturisticExecutionModal from '@/components/modals/FuturisticExecutionModal';
import { 
  Plus, 
  Search,
  Activity,
  Grid3x3,
  List
} from 'lucide-react';
import '@/styles/futuristic.css';

const Executions: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const base = location.pathname.startsWith('/dashboard') ? '/dashboard' : '';
  const { executions, undoDeleteExecution, hydrateFromDatabase, isLoading } = useDashboardServerStore();
  const { isAuthenticated, user, loading: authLoading } = useServerAuth();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'recent' | 'quality' | 'cost'>('recent');
  const [showNewExecutionModal, setShowNewExecutionModal] = useState(false);

  const executionsList = Array.from(executions.values());

  // Initial hydration: load executions once after auth if store is empty
  useEffect(() => {
    if (!authLoading && isAuthenticated && user && executions.size === 0 && !isLoading) {
      hydrateFromDatabase().catch((err) => {
        console.error('Failed to hydrate executions on mount:', err);
      });
    }
  }, [authLoading, isAuthenticated, user, executions.size, isLoading, hydrateFromDatabase]);

  // Filter and sort executions
  const filteredExecutions = executionsList
    .filter(exec => {
      // Debug filtering
      const statusMatch = filterStatus === 'all' || exec.status === filterStatus;
      const searchMatch = !searchTerm || exec.id.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!statusMatch) {
        console.log(`Filtering out ${exec.id} - status ${exec.status} doesn't match filter ${filterStatus}`);
      }
      
      return statusMatch && searchMatch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'quality':
          return b.avg_quality_score - a.avg_quality_score;
        case 'cost':
          return b.cost_incurred - a.cost_incurred;
        default: // recent
          return b.created_at.localeCompare(a.created_at);
      }
    });

  // Status counts
  const statusCounts = {
    all: executionsList.length,
    running: executionsList.filter(e => e.status === 'running').length,
    completed: executionsList.filter(e => e.status === 'completed').length,
    failed: executionsList.filter(e => e.status === 'failed').length,
    paused: executionsList.filter(e => e.status === 'paused').length
  };

  // Control Panel Component
  const ControlPanel = () => (
    <div className="mb-6">
      <HolographicCard glow="cyan" intensity="subtle">
        <CardContent>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-cyan-400/50" />
              <input
                type="text"
                placeholder="Search executions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-black/30 border border-cyan-500/20 rounded-lg
                         text-white/90 placeholder-white/30 font-mono text-sm
                         focus:outline-none focus:border-cyan-500/50 focus:bg-black/50
                         transition-all"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(statusCounts).map(([status, count]) => {
              const isActive = filterStatus === status;
              return (
                <button
                  key={status}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Filter button clicked:', status);
                    setFilterStatus(status);
                  }}
                  className={`
                    relative px-3 py-2 rounded-lg border transition-all text-xs font-mono uppercase
                    ${isActive
                      ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400' 
                      : 'bg-black/20 border-white/10 text-white/50 hover:border-cyan-500/30 hover:text-cyan-400/70 hover:bg-cyan-500/10'}
                  `}
                  style={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    zIndex: 1
                  }}
                >
                  {status} ({count})
                </button>
              );
            })}
          </div>

          {/* View Controls */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Grid view button clicked');
                setViewMode('grid');
              }}
              className={`p-2 rounded-lg transition-all cursor-pointer ${
                viewMode === 'grid' ? 
                'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 
                'text-white/30 hover:text-white/50 hover:bg-white/5 border border-transparent'
              }`}
            >
              <Grid3x3 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('List view button clicked');
                setViewMode('list');
              }}
              className={`p-2 rounded-lg transition-all cursor-pointer ${
                viewMode === 'list' ? 
                'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 
                'text-white/30 hover:text-white/50 hover:bg-white/5 border border-transparent'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          {/* Sort Dropdown */}
          <select
            value={sortBy}
            onChange={(e) => {
              const newValue = e.target.value as 'recent' | 'quality' | 'cost';
              console.log('Sort dropdown changed to:', newValue);
              setSortBy(newValue);
            }}
            className="px-3 py-2 bg-black/30 border border-white/10 rounded-lg
                     text-white/70 font-mono text-sm
                     focus:outline-none focus:border-cyan-500/50
                     hover:border-cyan-500/30 hover:bg-cyan-500/5
                     cursor-pointer transition-all"
          >
            <option value="recent" className="bg-black text-white">Recent</option>
            <option value="quality" className="bg-black text-white">Quality</option>
            <option value="cost" className="bg-black text-white">Cost</option>
          </select>
          </div>
        </CardContent>
      </HolographicCard>
    </div>
  );

  // Calculate stats for the bar
  const totalCost = executionsList.reduce((sum, exec) => sum + exec.cost_incurred, 0);
  const avgQuality = executionsList.length > 0 ? 
    executionsList.reduce((sum, exec) => sum + exec.avg_quality_score, 0) / executionsList.length : 0;
  const successRate = executionsList.length > 0 ?
    (statusCounts.completed / executionsList.length) * 100 : 0;

  // Debug filter changes
  useEffect(() => {
    console.log('Filter status changed to:', filterStatus);
  }, [filterStatus]);

  useEffect(() => {
    console.log('Sort changed to:', sortBy);
  }, [sortBy]);

  useEffect(() => {
    console.log('View mode changed to:', viewMode);
  }, [viewMode]);

  // Ctrl/Cmd+Z to undo last delete
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const isUndo = (isMac ? e.metaKey : e.ctrlKey) && (e.key === 'z' || e.key === 'Z');
      if (isUndo) {
        e.preventDefault();
        undoDeleteExecution();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undoDeleteExecution]);

  useEffect(() => {
    console.log('Total executions:', executionsList.length);
    console.log('Filtered executions:', filteredExecutions.length);
    console.log('Current filter:', filterStatus);
  }, [executionsList.length, filteredExecutions.length, filterStatus]);

  // Empty State
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-20">
      <Activity className="w-16 h-16 text-cyan-400/30 mb-4" />
      <h3 className="text-xl font-light text-white/50 mb-2">No Executions Found</h3>
      <p className="text-sm text-white/30 mb-6">
        {searchTerm ? 'Try adjusting your search criteria' : 'Start your first pipeline execution'}
      </p>
      {!searchTerm && (
        <button 
          className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10"
          onClick={() => setShowNewExecutionModal(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          NEW EXECUTION
        </button>
      )}
    </div>
  );

  return (
    <div className="void-foundation min-h-screen">
      <AtmosphericBackground />
      
      <div className="relative z-10 p-6">
        
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-light text-gradient-cyan tracking-wider mb-2">
            MISSION CONTROL
          </h1>
          <p className="text-white/50 font-mono text-sm mb-6">
            ACTIVE PIPELINE EXECUTIONS & ARCHIVES
          </p>
          
          {/* Stats Bar - Enhanced rectangular display */}
          <StatsBar 
            metrics={{
              activeExecutions: statusCounts.running,
              totalCost: totalCost,
              successRate: successRate,
              avgQuality: avgQuality,
              runningCount: statusCounts.running,
              completedCount: statusCounts.completed
            }}
            className=""
          />
        </div>

        {/* Control Panel */}
        <ControlPanel />

        {/* Executions Grid/List */}
        {filteredExecutions.length > 0 ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredExecutions.map((execution) => (
                <MissionCard
                  key={execution.id}
                  execution={execution}
                  onClick={() => navigate(`${base}/portal/${execution.id}`)}
                  className="fade-in"
                />
              ))}
            </div>
          ) : (
            <HolographicCard glow="blue" intensity="subtle">
              <CardContent className="p-0">
                <div className="divide-y divide-white/5">
                  {filteredExecutions.map((execution) => (
                    <div
                      key={execution.id}
                      className="p-4 hover:bg-cyan-500/5 cursor-pointer transition-all group"
                      onClick={() => navigate(`${base}/portal/${execution.id}`)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`
                            w-2 h-2 rounded-full
                            ${execution.status === 'running' ? 'bg-cyan-400 animate-pulse' :
                              execution.status === 'completed' ? 'bg-success-400' :
                              execution.status === 'failed' ? 'bg-error-400' :
                              'bg-gray-400'}
                          `} />
                          <div>
                            <div className="font-mono text-sm text-white/80">
                              {execution.id.slice(0, 8)}
                            </div>
                            <div className="text-xs text-white/40 mt-1">
                              {execution.current_stage} • Q:{Math.round(execution.avg_quality_score)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-sm font-mono text-cyan-400/70">
                              ${(execution.cost_incurred || 0).toFixed(2)}
                            </div>
                            <div className="text-xs text-white/30">
                              {Math.round(execution.progress.overall * 100)}%
                            </div>
                          </div>
                          <Activity className="w-4 h-4 text-white/20 group-hover:text-cyan-400/50 transition-colors" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </HolographicCard>
          )
        ) : (
          <EmptyState />
        )}
      </div>

      {/* Floating Action Orb */}
      <div 
        className="action-orb"
        onClick={() => setShowNewExecutionModal(true)}
      >
        <Plus className="w-6 h-6 text-white" />
      </div>

      {/* New Execution Modal */}
      <FuturisticExecutionModal
        isOpen={showNewExecutionModal}
        onClose={() => setShowNewExecutionModal(false)}
      />
    </div>
  );
};

export default Executions;